import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import Badge from 'components/generic/Badge.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import Tooltip from 'components/generic/Tooltip.component';
import TooltipContentReceptionist from 'components/generic/TooltipContentReceptionist.component';
import CONF from 'config/conf.singleton';
import { HELP_LINKS } from 'constants/helpCenterLinks.const';
import { withTranslation } from 'providers/GILT.provider';
import { withTheme } from 'providers/Theme.provider';
import { selectCurrentSalonCountryCode, selectCurrentSalonOnlineBooking } from 'selectors/currentSalon.selectors';
import { isEffectiveReceptionist } from 'selectors/user';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import CN from './FacebookConnect.module.scss';

const L = logger('FacebookConnect');

const FACEBOOK_GRAPH_VERSION = '10.0';
const FACEBOOK_CLIENT_ID = 2815615545366839;
const FACEBOOK_REDIRECT_URI = CONF.salonUrl;
const LINK = 'https://help.getslick.com/en/articles/5351439-add-a-book-now-button-to-your-facebook-and-instagram';

const USE_LINK = false;

const deleteFb = async (url) =>
  fetch(url, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });

function safeCtaUrlKeepHash(rawUrl, utm) {
  const s = String(rawUrl || '').trim();
  const hashIdx = s.indexOf('#');
  const base = hashIdx >= 0 ? s.slice(0, hashIdx) : s;
  const frag = hashIdx >= 0 ? s.slice(hashIdx) : '';
  const u = new URL(base);
  if (utm) u.searchParams.set('utm', utm);
  const out = `${u.toString()}${frag}`;
  return out;
}

const generateExtras = ({ groupNcobTinyUrl, url, salonId, salonName }) => {
  const onlineBookingUrl = L.info('generateExtras()', 'onlineBookingUrl:', groupNcobTinyUrl || url);
  const base = (onlineBookingUrl || url || '').trim();
  const fbCtaUrl = safeCtaUrlKeepHash(base, 'facebook');
  const igCtaUrl = safeCtaUrlKeepHash(base, 'instagram');

  return {
    setup: {
      timezone: 'Europe/London',
      currency: 'GBP',
      business_vertical: 'APPOINTMENTS',
      external_business_id: salonId,
    },
    business_config: {
      business: {
        name: salonName,
      },
      page_cta: {
        enabled: true,
        cta_button_text: 'Book Now',
        cta_button_url: fbCtaUrl,
        below_button_text: 'Powered by Slick',
      },
      ig_cta: {
        enabled: true,
        cta_button_text: 'Book Now',
        cta_button_url: igCtaUrl,
      },
      messenger_menu: {
        enabled: true,
        cta_button_text: 'Book Now',
        cta_button_url: fbCtaUrl,
      },
    },
    repeat: false,
  };
};

const generateConnectUrl = ({ groupNcobTinyUrl, url, salonId, salonName }) => {
  const extras = generateExtras({ groupNcobTinyUrl, url, salonId, salonName });

  const params = new URLSearchParams({
    client_id: String(FACEBOOK_CLIENT_ID),
    display: 'page',
    redirect_uri: FACEBOOK_REDIRECT_URI,
    response_type: 'token',
    scope: 'manage_business_extension',
    state: String(salonId),
    extras: JSON.stringify(extras),
  });

  const fbConnectUrl = `https://www.facebook.com/dialog/oauth?${params.toString()}`;
  L.info('generateConnectUrl()', 'fbConnectUrl:', fbConnectUrl);

  return fbConnectUrl;
};

const generateDeleteUrl = ({ salonId, token }) => {
  const fbDeleteUrl = `https://graph.facebook.com/v${FACEBOOK_GRAPH_VERSION}/fbe_business/fbe_installs?fbe_external_business_id=${salonId}&access_token=${token}`;
  return L.info('generateDeleteUrl()', 'fbDeleteUrl:', fbDeleteUrl);
};

class FacebookConnect extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isConnected: false,
      deleting: false,
    };
  }

  componentDidMount() {
    this.setIsConnected(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setIsConnected(nextProps);
  }

  setIsConnected(props) {
    const { fb_details: detailsFromProps } = props;
    const { fb_access_token: token } = asObject(detailsFromProps);

    const hasDetails = detailsFromProps && Object.keys(detailsFromProps).length > 0;

    if (hasDetails) {
      this.setState({ isConnected: token !== null }); // eslint-disable-line react/no-did-mount-set-state
    } else if (!token) {
      this.setState({ isConnected: false }); // eslint-disable-line react/no-did-mount-set-state
    }
  }

  handleConnect = () => {
    const { salonId } = this.props;
    const { group_ncob_tiny_url: groupNcobTinyUrl, url, salonName } = this.props;

    const fbConnectUrl = generateConnectUrl({ groupNcobTinyUrl, url, salonId, salonName });

    window.location.replace(fbConnectUrl);
  };

  handleDisconnect = () => {
    const FTAG = 'handleDisconnect()';
    L.info(FTAG);

    const { removeFBIntegration, fb_details: detailsFromProps, salonId } = this.props;
    const { fb_access_token: token } = asObject(detailsFromProps);

    const hasDetails = detailsFromProps && Object.keys(detailsFromProps).length !== 0;

    this.setState({ deleting: true });
    const deleteFbUrl = generateDeleteUrl({ salonId, token });

    deleteFb(deleteFbUrl)
      .then(() => void removeFBIntegration(salonId.toString()))
      .catch((e) => void L.warn(FTAG, 'Having problems removing integration from Facebook', e));

    // FIXME: don't use setInterval, use promises
    const interval = setInterval(() => {
      if (hasDetails) {
        return;
      }
      this.setState({ deleting: false });
      clearInterval(interval);
      this.setState({ isConnected: false });
    }, 500);
  };

  render() {
    const { isConnected, deleting } = this.state;
    const { onlineBooking, isReceptionist, t, countryCode } = this.props;

    const helpLink = HELP_LINKS[countryCode].contactUsFacebookHelpLink || HELP_LINKS.GB.contactUsFacebookHelpLink;

    const componentClasses = cn({
      [CN.component]: true,
      [CN.darkMode]: this.props.isDarkMode,
    });

    return (
      <div data-bem="FacebookConnect" className={componentClasses}>
        {deleting && <OverlaySpinner />}

        <div className={CN.content}>
          {!isConnected && (
            <>
              <div className={CN.title}>{t('onlineBooking.generalSettings.socialWebsiteLinks.facebookConnect.title')}</div>
              <div className={CN.text}>{t('onlineBooking.generalSettings.socialWebsiteLinks.facebookConnect.description1')}</div>
              <div className={CN.buttonsContainer}>
                {isReceptionist ? (
                  <Tooltip variant="top" content={<TooltipContentReceptionist />}>
                    <Button variant="fb" disabled={!onlineBooking}>
                      <div className={CN.buttonInner}>
                        <Icon variant="fbWhite" width="lg" />
                        <span className={CN.gapMiddle}>{t('onlineBooking.generalSettings.socialWebsiteLinks.facebookConnect.facebookLogin')}</span>
                      </div>
                    </Button>
                  </Tooltip>
                ) : (
                  <Button onClick={this.handleConnect} variant="fb" disabled={!onlineBooking}>
                    <div className={CN.buttonInner}>
                      <Icon variant="fbWhite" width="lg" />
                      <span className={CN.gapMiddle}>{t('onlineBooking.generalSettings.socialWebsiteLinks.facebookConnect.facebookLogin')}</span>
                    </div>
                  </Button>
                )}
                {USE_LINK && (
                  <ExternalLink link={LINK} variant="primary">
                    {t('onlineBooking.generalSettings.needHelp')}
                  </ExternalLink>
                )}
              </div>
              <div className={CN.notes}>
                {t('onlineBooking.generalSettings.socialWebsiteLinks.facebookConnect.description2Part1')}
                <ExternalLink link={helpLink} variant="inline">
                  <span className={CN.textLink}>{t('onlineBooking.generalSettings.socialWebsiteLinks.facebookConnect.description2Part2')}</span>
                </ExternalLink>
              </div>
            </>
          )}

          {isConnected && (
            <div className={CN.content}>
              <div className={CN.title}>
                {t('onlineBooking.generalSettings.socialWebsiteLinks.facebookConnect.title')}
                <div className={CN.linkContainer}>
                  <ExternalLink link="https://www.facebook.com/" variant="primary">
                    <div className={CN.gapSmall}>
                      <Badge variant="fb">
                        <div className={CN.gapSmall}>
                          <Icon variant="fbWhite" width="md" />
                        </div>
                        <Icon variant="instagram" />
                        <div className={CN.fbActiveText}>{t('onlineBooking.generalSettings.socialWebsiteLinks.facebookConnect.conectFacebook')}</div>
                      </Badge>
                    </div>
                  </ExternalLink>
                </div>
              </div>

              <div className={CN.text}>{t('onlineBooking.generalSettings.socialWebsiteLinks.facebookConnect.description3')}</div>

              <div className={CN.buttonsContainer}>
                {isReceptionist ? (
                  <Tooltip variant="top" content={<TooltipContentReceptionist />}>
                    <Button variant="primary">
                      <span className={CN.gapMiddle}>{t('onlineBooking.generalSettings.socialWebsiteLinks.facebookConnect.disconnect')}</span>
                    </Button>
                  </Tooltip>
                ) : (
                  <Button onClick={this.handleDisconnect} variant="primary">
                    <span className={CN.gapMiddle}>{t('onlineBooking.generalSettings.socialWebsiteLinks.facebookConnect.disconnect')}</span>
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={CN.imageContainer} />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  onlineBooking: selectCurrentSalonOnlineBooking(state),
  isReceptionist: isEffectiveReceptionist(state),
  countryCode: selectCurrentSalonCountryCode(state),
});

const FacebookConnectHoc = connect(mapStateToProps)(withTranslation()(withTheme()(FacebookConnect)));
export default FacebookConnectHoc;
